import styled from 'styled-components';
import SetupResourcesPlayer from 'components/players/SetupResourcesPlayer';
import getFingerprint from 'lib/fingerprint';
import { logger } from '@finance/shared';
import { signIn } from 'next-auth/react';
import { useAuthLink } from 'lib/comlink/AuthLink';
import { useCallback, useEffect } from 'react';
import appConfig from 'config/appConfig';
import { useRouter } from 'next/router';
import { decode as nextAuthDecodeToken } from 'next-auth/jwt';
import HoneybookApi from 'lib/hb/honeybook.api';
import { setColorModeParam } from 'lib/colorMode';
import { defineRequestContextualStore, updateContextualStore } from 'lib/contextualStore';

import type { NextPage } from 'next';
import type { GetServerSidePropsContext } from 'next/types';
import type { HBAuthMessage } from 'lib/comlink/types';
import type { Session } from 'next-auth';

const TOKEN_COOKIE_NAME = 'next-auth.session-token';

interface IProps {
    isAuthDisabled?: boolean;
}

const Embedded: NextPage<IProps> = props => {
    const adapter = useAuthLink();
    const router = useRouter();

    const signInDev = useCallback(async () => {
        const { callbackUrl, source, color_mode } = router.query;

        const fullCallbackUrl = buildCallbackUrl({ callbackUrl, source, color_mode } as CallbackUrlParams);
        await signIn('hbTokenAuthenticationDev', {
            fingerprint: await getFingerprint(),
            admin: false,
            redirect: !!fullCallbackUrl,
            callbackUrl: fullCallbackUrl as string
        });
    }, [router.query]);

    useEffect(() => {
        if (props.isAuthDisabled) {
            signInDev();
            return;
        }
        adapter?.getToken();
    }, [adapter, props.isAuthDisabled, signInDev]);

    return (
        <LoaderWrapper>
            <SetupResourcesPlayer />
        </LoaderWrapper>
    );
};

type CallbackUrlParams = Pick<HBAuthMessage, 'callbackUrl' | 'source'> & { color_mode?: string };

function buildCallbackUrl(data: CallbackUrlParams) {
    const { callbackUrl, source, color_mode } = data;

    let fullCallbackUrl = callbackUrl as string;
    if (source) {
        const url = new URL(fullCallbackUrl, window.location.origin);
        url.searchParams.append('source', source as string);
        fullCallbackUrl = url.toString();
    }
    if (fullCallbackUrl) {
        fullCallbackUrl = setColorModeParam(fullCallbackUrl, color_mode);
    }

    return fullCallbackUrl;
}

const LoaderWrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
`;

export default Embedded;

// Server Side Functions

export const getServerSideProps = async ({ req }: GetServerSidePropsContext) => {
    const { isDevelopmentEnv, isAuthDisabled } = appConfig;

    defineRequestContextualStore({
        contextName: 'Embedded Page',
        req
    });

    const redirectUrl = await getRedirectUrl(req);

    if (redirectUrl) {
        return {
            redirect: {
                permanent: false,
                destination: redirectUrl
            }
        };
    }

    return {
        props: {
            isAuthDisabled: isDevelopmentEnv && isAuthDisabled()
        }
    };
};

async function getRedirectUrl(request: GetServerSidePropsContext['req']) {
    if (appConfig.forceAuthRoutes) {
        logger.debug('[Early Redirect Check] disabled, skipping');

        return false;
    }
    const searchParams = new URLSearchParams(request.url?.split('?')[1] || '');

    const callback = searchParams.get('callbackUrl');
    const companyId = searchParams.get('company_id');
    const colorMode = searchParams.get('color_mode');

    // Since next auth isnt loaded for this route, we need to decode the token
    const token = (await nextAuthDecodeToken({
        token: request.cookies[TOKEN_COOKIE_NAME] || request.cookies['__Secure-' + TOKEN_COOKIE_NAME],
        secret: process.env.NEXTAUTH_SECRET as string
    })) as unknown as Session;

    logger.debug(
        {
            hasToken: Boolean(token),
            isAdmin: isAdmin(token),
            isBookkeeper: isBookkeeper(token),
            callback: callback?.toString() || 'missing',
            url: request.url
        },
        '[Early Redirect Check] log values'
    );

    if (
        token &&
        // Malkut login-as users can't use this feature
        !isAdmin(token) &&
        // In case of multi-brand, if a user swapped company
        !hasSwappedCompany(token, companyId) &&
        // Bookkeepers can't use this feature
        !isBookkeeper(token) &&
        // We can only redirect if we know what the redirect target is
        callback
    ) {
        updateContextualStore(request, token);
        // check if the token is valid against HPD only if all other conditions are met
        // this is only to make sure the user doesnt have a stale token. This doesnt replace the
        // normal validation all our requests go through
        if (await validateToken(token)) {
            logger.info({ callback, id: token?.user?._id }, '[Early Redirect Check] redirecting user');
            return setColorModeParam(callback, colorMode);
        }
    }
    return null;
}

async function validateToken(token: Session | undefined) {
    const api = new HoneybookApi(token);

    const user = token?.user;
    const hbToken = token?.user.hbData.hbToken || '';
    const userId = user?._id || '';

    try {
        const response = await api.validateUserToken({ userId, hbToken });

        if (response.status == 204) {
            logger.debug({ userId }, '[Early Redirect Check] token valid');
            return true;
        }
        logger.debug({ userId }, '[Early Redirect Check] invalid token');
    } catch (e) {
        logger.error({ error: e, userId }, '[Early Redirect Check] error while validating token');
    }
    return false;
}

function isAdmin(token: Session | null): boolean {
    return Boolean(token?.user?.admin);
}

function isBookkeeper(token: Session | undefined) {
    return Boolean(token?.user.isBookkeeperMode);
}

function hasSwappedCompany(token: Session | undefined, companyId?: string | null) {
    return token?.user.companyId !== companyId;
}
